import { createFileRoute } from "@tanstack/react-router";
import { useAuth } from "@/utils/auth";
import { hooks as h } from "@/hooks";
import { ui } from "@patient-watch/ui";
import { Badge } from "@patient-watch/ui/badge";
import { Switch } from "@patient-watch/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@patient-watch/ui/table";
import { Loader2 } from "lucide-react";
import UnauthorizedPage from "@/components/pages/UnauthorizedPage";
import {
    shockwaveContentsBuilderQueryOptions,
    useShockwaveContentsBuilderQuery,
} from "@/features/shockwave/api/selectShockwaveContentsForBuilder";
import { useUpdateShockwaveContentIsPublic } from "@/features/shockwave/api/updateShockwaveContentIsPublic";
import { useMemo } from "react";

const ShockwaveContentsBuilderPage = () => {
    const auth = useAuth();
    const { mutate: updateIsPublic } = useUpdateShockwaveContentIsPublic();

    h.useNav([
        { label: "Builder", route: { to: "/builder" } },
        { label: "Shockwave contents", route: { to: "/builder/shockwave-contents" } },
    ]);

    const { data: contents, isLoading } = useShockwaveContentsBuilderQuery();
    const rows = useMemo(
        () =>
            [...(contents ?? [])].sort(
                (a, b) =>
                    a.shockwave_type.localeCompare(b.shockwave_type) ||
                    a.impulses - b.impulses ||
                    a.dose - b.dose ||
                    a.unit.localeCompare(b.unit)
            ),
        [contents]
    );

    if (!auth?.roles?.isAdmin && !auth?.isSuperUser) {
        return <UnauthorizedPage message="Admin Only Page" />;
    }

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-2 md:p-4">
            <div>
                <h1 className="font-semibold text-3xl tracking-tight">Shockwave contents</h1>
                <p className="text-muted-foreground">
                    Manage shockwave type/impulses/dose/unit options available in diaries. Public options are offered to
                    every clinician with public shockwave access; private options only to the person who created them.
                </p>
            </div>

            <ui.Card>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Type</TableHead>
                            <TableHead>Impulses</TableHead>
                            <TableHead>Dose</TableHead>
                            <TableHead>Unit</TableHead>
                            <TableHead>Public</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.length ? (
                            rows.map((row) => (
                                <TableRow key={`${row.shockwave_type}::${row.impulses}::${row.dose}::${row.unit}`}>
                                    <TableCell>{row.shockwave_type}</TableCell>
                                    <TableCell className="text-muted-foreground">{String(row.impulses)}</TableCell>
                                    <TableCell className="text-muted-foreground">{String(row.dose)}</TableCell>
                                    <TableCell>{row.unit}</TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <Switch
                                                aria-label={`Public: ${row.shockwave_type} ${row.impulses} ${row.dose} ${row.unit}`}
                                                checked={row.is_public === true}
                                                onCheckedChange={(checked) =>
                                                    updateIsPublic({
                                                        key: {
                                                            shockwave_type: row.shockwave_type,
                                                            impulses: row.impulses,
                                                            dose: row.dose,
                                                            unit: row.unit,
                                                        },
                                                        is_public: checked === true,
                                                    })
                                                }
                                            />
                                            <Badge
                                                variant={row.is_public ? "default" : "secondary"}
                                                className="text-xs"
                                            >
                                                {row.is_public ? "Yes" : "No"}
                                            </Badge>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} className="py-12 text-muted-foreground text-center">
                                    No shockwave contents found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </ui.Card>
        </div>
    );
};

export const Route = createFileRoute("/_app/builder/shockwave-contents/")({
    component: ShockwaveContentsBuilderPage,
    loader: async ({ context }) => {
        await context.queryClient.ensureQueryData(shockwaveContentsBuilderQueryOptions());
    },
});
